import { resolveCurrentDeviceKey } from 'src/utils/resolveCurrentDeviceKey'
import { readInitialDeviceUserId } from 'src/utils/resolveUpgradeDeviceId'

/** 安全解析 JSON 字符串，失败时返回 fallback */
const safeParse = (raw, fallback = {}) => {
  try {
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

/** 将值规范化为去首尾空格的字符串，null/undefined 转为空串 */
const normalize = value => (value == null ? '' : String(value).trim())

/**
 * 生成一次设备切换的唯一 token，用于 requestKey 防竞态。
 * @returns {string}
 */
export const createSwitchToken = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

/**
 * 从 deviceUser / currentDevice / currentMode 组装 deviceContext。
 * @param {Object} [options]
 * @param {Object} [options.deviceUser] - localStorage DeviceUser 对象
 * @param {Object} [options.currentDevice] - localStorage currentDevice 对象
 * @param {string} [options.currentMode] - 平台模式，如 iOS / Android-Cloud / Android-Local
 * @param {string|number} [options.accountId] - 账号 device_id，缺省从 deviceUser 解析
 * @param {string} [options.switchToken='initial'] - 本次切换 token
 * @returns {{ accountId: string, username: string, currentMode: string, physicalDeviceKey: string, identityKey: string, switchToken: string, requestKey: string }}
 */
export const buildDeviceContext = ({
  deviceUser = {},
  currentDevice = {},
  currentMode = '',
  accountId,
  switchToken = 'initial'
} = {}) => {
  const resolvedAccountId = normalize(
    accountId ?? deviceUser?.device_id ?? deviceUser?.id
  )
  const username = normalize(deviceUser?.username)
  const mode = normalize(currentMode)
  const physicalDeviceKey = normalize(resolveCurrentDeviceKey(currentDevice))
  const accountKey = resolvedAccountId || username || '-'
  const identityKey = `${mode || '-'}:${accountKey}:${physicalDeviceKey || '-'}`
  const token = normalize(switchToken) || 'initial'

  return {
    accountId: resolvedAccountId,
    username,
    currentMode: mode,
    physicalDeviceKey,
    identityKey,
    switchToken: token,
    requestKey: `${identityKey}@${token}`
  }
}

/**
 * 从 localStorage 读取并重建 deviceContext（SSR 时返回空上下文）。
 * @param {Object} [options]
 * @param {string} [options.switchToken] - 指定 token，缺省读已存 deviceContext
 * @returns {ReturnType<typeof buildDeviceContext>}
 */
export const readStoredDeviceContext = ({ switchToken } = {}) => {
  if (typeof window === 'undefined') {
    return buildDeviceContext({ switchToken })
  }

  const deviceUser = safeParse(localStorage.getItem('DeviceUser'))
  const currentDevice = safeParse(localStorage.getItem('currentDevice'))
  const storedContext = safeParse(localStorage.getItem('deviceContext'))

  return buildDeviceContext({
    deviceUser,
    currentDevice,
    currentMode: localStorage.getItem('currentMode') || '',
    accountId: readInitialDeviceUserId(),
    switchToken: switchToken || storedContext.switchToken || 'initial'
  })
}
